"use client";

import { useState, useMemo, useEffect, Fragment } from "react";
import CommonHeader from "./common/CommonHeader";
import { ActivitySearchBar } from "./activities/ActivitySearchBar";
import { ActivityFilter } from "./activities/ActivityFilter";
import { activityService } from "@/services/activityService";
import { StaffActivity } from "@/types/activity.types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { ArrowUpDown, ChevronDown, ChevronRight } from "lucide-react";
import { formatRelativeTime } from "@/lib/utils";

const actionColors: Record<string, string> = {
  CREATE: "bg-green-50 text-green-700 border-green-100",
  UPDATE: "bg-blue-50 text-blue-700 border-blue-100",
  DELETE: "bg-red-50 text-red-700 border-red-100",
  LOGIN: "bg-emerald-50 text-emerald-700 border-emerald-100",
  LOGOUT: "bg-amber-50 text-amber-700 border-amber-100",
};

const moduleColors: Record<string, string> = {
  STAFF: "bg-orange-50 text-orange-700 border-orange-100",
  STAFF_TIME_TRACKING: "bg-yellow-50 text-yellow-700 border-yellow-100",
  APPOINTMENT: "bg-indigo-50 text-indigo-700 border-indigo-100",
};

function parseActivityData(data: unknown): Record<string, unknown> | null {
  if (!data || data === "null" || data === null) return null;
  if (typeof data === "string") {
    try { return JSON.parse(data); } catch { return null; }
  }
  if (typeof data === "object") return data as Record<string, unknown>;
  return null;
}

function ActivityDataView({ oldData, newData, action }: { oldData: unknown; newData: unknown; action: string }) {
  const parsedOld = parseActivityData(oldData);
  const parsedNew = parseActivityData(newData);

  if (action === "CREATE" && parsedNew) {
    return (
      <div className="space-y-2">
        <div className="text-xs font-semibold text-green-700 mb-2">New Data:</div>
        <div className="bg-green-50 border border-green-200 rounded-md p-3">
          <pre className="text-xs overflow-x-auto whitespace-pre-wrap">{JSON.stringify(parsedNew, null, 2)}</pre>
        </div>
      </div>
    );
  }
  if (action === "DELETE" && parsedOld) {
    return (
      <div className="space-y-2">
        <div className="text-xs font-semibold text-red-700 mb-2">Deleted Data:</div>
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <pre className="text-xs overflow-x-auto whitespace-pre-wrap">{JSON.stringify(parsedOld, null, 2)}</pre>
        </div>
      </div>
    );
  }
  if (action === "UPDATE") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="text-xs font-semibold text-gray-700 mb-2">Old Data:</div>
          {parsedOld ? (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
              <pre className="text-xs overflow-x-auto whitespace-pre-wrap">{JSON.stringify(parsedOld, null, 2)}</pre>
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-xs text-muted-foreground">No previous data</div>
          )}
        </div>
        <div className="space-y-2">
          <div className="text-xs font-semibold text-blue-700 mb-2">New Data:</div>
          {parsedNew ? (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <pre className="text-xs overflow-x-auto whitespace-pre-wrap">{JSON.stringify(parsedNew, null, 2)}</pre>
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-xs text-muted-foreground">No new data</div>
          )}
        </div>
      </div>
    );
  }
  return null;
}

export function StaffActivityManagement() {
  const [activities, setActivities] = useState<StaffActivity[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterAction, setFilterAction] = useState<string>("all");
  const [filterModule, setFilterModule] = useState<string>("all");
  const [sortConfig, setSortConfig] = useState<{
    key: "createdAt" | "action" | "module" | "description";
    direction: "asc" | "desc";
  }>({ key: "createdAt", direction: "desc" });
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    activityService.getStaffActivities().then(setActivities);
  }, []);

  const filteredAndSorted = useMemo(() => {
    let filtered = activities.filter((a) => {
      const matchesSearch =
        searchTerm === "" ||
        a.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.module.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (a.staffName ?? "").toLowerCase().includes(searchTerm.toLowerCase());
      const matchesAction = filterAction === "all" || a.action === filterAction;
      const matchesModule = filterModule === "all" || a.module === filterModule;
      return matchesSearch && matchesAction && matchesModule;
    });

    filtered.sort((a, b) => {
      let aVal: string | Date;
      let bVal: string | Date;
      switch (sortConfig.key) {
        case "createdAt": aVal = new Date(a.createdAt); bVal = new Date(b.createdAt); break;
        case "action": aVal = a.action; bVal = b.action; break;
        case "module": aVal = a.module; bVal = b.module; break;
        case "description": aVal = a.description; bVal = b.description; break;
        default: return 0;
      }
      if (aVal instanceof Date && bVal instanceof Date) {
        return sortConfig.direction === "asc" ? aVal.getTime() - bVal.getTime() : bVal.getTime() - aVal.getTime();
      }
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [activities, searchTerm, filterAction, filterModule, sortConfig]);

  const totalPages = Math.ceil(filteredAndSorted.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginated = filteredAndSorted.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) setCurrentPage(1);
  }, [filteredAndSorted.length, currentPage, totalPages]);

  const handleSort = (key: "createdAt" | "action" | "module" | "description") => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const hasData = (a: StaffActivity) =>
    Boolean(a.oldData && a.oldData !== "null") || Boolean(a.newData && a.newData !== "null");

  const getPageNumbers = () => {
    const pages: (number | "ellipsis")[] = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage <= 3) {
        for (let i = 2; i <= 4; i++) pages.push(i);
        pages.push("ellipsis");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push("ellipsis");
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push("ellipsis");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push("ellipsis");
        pages.push(totalPages);
      }
    }
    return pages;
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div className="space-y-6 w-full overflow-x-hidden">
      <CommonHeader
        title="Staff Activity"
        description="Monitor all staff login, logout, and action history"
        showButton={false}
      />

      <div className="grid gap-4 md:grid-cols-3">
        <ActivitySearchBar searchTerm={searchTerm} onSearchChange={setSearchTerm} />
        <ActivityFilter
          filterAction={filterAction}
          filterModule={filterModule}
          onFilterActionChange={setFilterAction}
          onFilterModuleChange={setFilterModule}
        />
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold">
              Staff Activities ({filteredAndSorted.length})
            </CardTitle>
            {filteredAndSorted.length > 0 && (
              <div className="text-sm text-muted-foreground">
                Showing {startIndex + 1} - {Math.min(startIndex + itemsPerPage, filteredAndSorted.length)} of {filteredAndSorted.length}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b hover:bg-transparent">
                  <TableHead className="w-10"></TableHead>
                  <TableHead className="text-xs font-medium">Staff</TableHead>
                  <TableHead className="text-xs font-medium">
                    <Button variant="ghost" size="sm" onClick={() => handleSort("action")} className="h-8 px-2">
                      Action <ArrowUpDown className="ml-1 h-3 w-3" />
                    </Button>
                  </TableHead>
                  <TableHead className="text-xs font-medium">
                    <Button variant="ghost" size="sm" onClick={() => handleSort("module")} className="h-8 px-2">
                      Module <ArrowUpDown className="ml-1 h-3 w-3" />
                    </Button>
                  </TableHead>
                  <TableHead className="text-xs font-medium">
                    <Button variant="ghost" size="sm" onClick={() => handleSort("description")} className="h-8 px-2">
                      Description <ArrowUpDown className="ml-1 h-3 w-3" />
                    </Button>
                  </TableHead>
                  <TableHead className="text-xs font-medium">
                    <Button variant="ghost" size="sm" onClick={() => handleSort("createdAt")} className="h-8 px-2">
                      Date <ArrowUpDown className="ml-1 h-3 w-3" />
                    </Button>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginated.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      No staff activities found
                    </TableCell>
                  </TableRow>
                ) : (
                  paginated.map((activity) => {
                    const isExpanded = expandedRows.has(activity.id);
                    const showExpand = hasData(activity);
                    return (
                      <Fragment key={activity.id}>
                        <TableRow className="border-b">
                          <TableCell className="w-10">
                            {showExpand && (
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => toggleRow(activity.id)}>
                                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                              </Button>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="text-sm font-medium">{activity.staffName ?? "—"}</div>
                            {activity.staffEmail && (
                              <div className="text-xs text-muted-foreground">{activity.staffEmail}</div>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`text-xs ${actionColors[activity.action] ?? "bg-gray-50 text-gray-700 border-gray-100"}`}>
                              {activity.action}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`text-xs ${moduleColors[activity.module] ?? "bg-gray-50 text-gray-700 border-gray-100"}`}>
                              {activity.module}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">{activity.description}</TableCell>
                          <TableCell className="text-sm">
                            <div>{new Date(activity.createdAt).toLocaleString("en-GB", {
                              day: "2-digit", month: "short", year: "numeric",
                              hour: "2-digit", minute: "2-digit", hour12: false,
                            })}</div>
                            <div className="text-xs text-muted-foreground">
                              {formatRelativeTime(new Date(activity.createdAt))}
                            </div>
                          </TableCell>
                        </TableRow>
                        {showExpand && isExpanded && (
                          <TableRow>
                            <TableCell colSpan={6} className="bg-muted/30 p-4">
                              <ActivityDataView oldData={activity.oldData} newData={activity.newData} action={activity.action} />
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
          {filteredAndSorted.length > 0 && totalPages > 1 && (
            <div className="mt-4">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      onClick={(e) => { e.preventDefault(); handlePageChange(currentPage - 1); }}
                      className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                    />
                  </PaginationItem>
                  {getPageNumbers().map((page, idx) =>
                    page === "ellipsis" ? (
                      <PaginationItem key={`ellipsis-${idx}`}><PaginationEllipsis /></PaginationItem>
                    ) : (
                      <PaginationItem key={page}>
                        <PaginationLink
                          href="#"
                          onClick={(e) => { e.preventDefault(); handlePageChange(page); }}
                          isActive={currentPage === page}
                          className="cursor-pointer"
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    )
                  )}
                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      onClick={(e) => { e.preventDefault(); handlePageChange(currentPage + 1); }}
                      className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
